<script setup>
// M26.2 — "Live Workforce Event Timeline". Nexceo::Ai::WorkforceEventBus has
// no account-wide query method (events_for is per-lead only), so this is a
// faithful reconstruction from the same lifecycle events WorkforceTaskQueue
// actually publishes to that bus (task_created / task_completed /
// task_failed) — sourced from the account-wide workforce_tasks list instead
// of the bus directly. Parent merges/sorts task timestamps into `events`.
defineProps({
  events: { type: Array, required: true },
  // [{ id, type, icon, colorClass, title, detail, at }]
});

function relativeTime(iso) {
  const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 10) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="rounded-xl border border-n-weak bg-n-background overflow-hidden">
    <div class="px-4 py-3 border-b border-n-weak">
      <h2
        class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider flex items-center gap-1.5"
      >
        <span class="i-lucide-activity size-3.5" />
        Live Event Timeline
      </h2>
    </div>

    <div v-if="!events.length" class="flex flex-col items-center py-8 gap-1.5">
      <span class="i-lucide-radio size-6 text-n-slate-6" />
      <p class="text-xs text-n-slate-7">No workforce activity yet.</p>
    </div>

    <div v-else class="max-h-80 overflow-y-auto">
      <div
        v-for="event in events"
        :key="event.id"
        class="flex items-start gap-2.5 px-4 py-2.5 border-b border-n-weak last:border-0"
      >
        <span
          class="flex items-center justify-center w-6 h-6 rounded-full shrink-0 mt-0.5"
          :class="event.colorClass"
        >
          <span :class="event.icon" class="size-3" />
        </span>
        <div class="flex-1 min-w-0">
          <p class="text-xs text-n-slate-12 truncate">{{ event.title }}</p>
          <p class="text-[11px] text-n-slate-7 truncate">{{ event.detail }}</p>
        </div>
        <span class="text-[10px] text-n-slate-6 shrink-0 whitespace-nowrap">
          {{ relativeTime(event.at) }}
        </span>
      </div>
    </div>
  </div>
</template>
